import { leagueBase } from '../lib/route';
import { lazy, Suspense, useEffect, useState } from 'react';
import { api, type League, type StandingRow } from '../lib/api';
import Crest from '../components/Crest';
import SeasonStats from '../components/SeasonStats';
import ChampionsCard from '../components/ChampionsCard';
import LeagueDocs from '../components/LeagueDocs';
import { trimSharedPrefix } from '../lib/names';
import { hasPostableTable } from '../lib/share/postable';
import { ownerTermsFor } from '../lib/interest/owner-terms';
import { closesOn } from '../lib/squad-entry/rules';

/*
 * The poster renderer, fetched only when there is a table worth posting.
 *
 * It and its dependencies are about a hundred kilobytes, and they used to be
 * part of the first chunk every visitor downloaded before the app drew
 * anything — a player checking the table waiting on a renderer they would never
 * open. `hasPostableTable` below is deliberately dependency-free, so asking
 * whether to offer the button costs nothing.
 */
const TableShare = lazy(() => import('../components/TableShare'));

/**
 * Trims a stored decimal to what a reader wants: 44.50 -> 44.5, 5.00 -> 5.
 * Season 1's legacy scoring produced half-sets, so these are not all integers.
 */
const tidy = (n: number): string => String(Number(n.toFixed(2)));

/** A difference always carries its sign, so nought reads as level, not blank. */
const signed = (n: number): string => (n > 0 ? `+${tidy(n)}` : n < 0 ? tidy(n) : '0');

/**
 * How each tiebreaker reads in the note under the table.
 *
 * `playoff` is deliberately generic. How a league plays off is its own
 * business and is printed below in its own words when it is actually needed;
 * naming Vitalli's sudden-death here stated one league's rule as everyone's,
 * and read wrongly for a league whose decider is a 10-point match.
 */
const TIEBREAKER_LABEL: Record<string, string> = {
  head_to_head: 'head-to-head record between the tied sides',
  set_difference: 'set difference',
  game_difference: 'game difference',
  points_difference: 'points difference — points scored minus conceded',
  fixture_wins: 'most wins',
  fixture_losses: 'fewest losses',
  playoff: 'a playoff between them',
};

/**
 * A criterion this table has no wording for yet.
 *
 * Never the raw key. Norwood's three new criteria went live against a map that
 * had not been widened, and the table told its readers they were separated by
 * "points_difference, then fixture_wins" — so the fallback now at least reads
 * as English while the real wording is added.
 */
const readable = (key: string) => key.replace(/_/g, ' ');

export default function Standings({
  seasonId,
  league,
  seasonLogoUrl,
}: {
  seasonId?: number;
  league?: League | null;
  seasonLogoUrl?: string | null;
}) {
  const [rows, setRows] = useState<StandingRow[] | null>(null);
  /*
   * The shape the table image is built at. Story by default, as on the
   * countdown panel: the table is shared into the players' group far more often
   * than it is posted to a feed.
   */
  const [meta, setMeta] = useState<{
    season: string;
    seasonId: number;
    archived: boolean;
    tiebreakers: string[];
    seasonComplete: boolean;
    minGames: { minGames: number; penaltyPerGame: number } | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // For the foot of the poster. Fetched separately and quietly: a table is
  // still worth posting if this fails.
  const [sponsorLogoUrls, setSponsorLogoUrls] = useState<string[]>([]);

  useEffect(() => {
    setRows(null);
    setError(null);
    api
      .standings(seasonId)
      .then((data) => {
        setRows(data.standings);
        setMeta({
          season: data.season,
          seasonId: data.seasonId,
          archived: data.archived,
          tiebreakers: data.tiebreakers ?? [],
          seasonComplete: Boolean(data.seasonComplete),
          minGames: data.minGames ?? null,
        });
      })
      .catch((e: Error) => setError(e.message));

    api
      .sponsors(seasonId)
      .then((d) =>
        setSponsorLogoUrls(
          d.sponsors.map((x) => x.logoUrl).filter((u): u is string => Boolean(u)).slice(0, 4),
        ),
      )
      .catch(() => setSponsorLogoUrls([]));
  }, [seasonId]);

  const shortName = trimSharedPrefix((rows ?? []).map((r) => r.team_name));
  /*
   * Whether a Super Tie-Break counts as a set here, and if so on what terms.
   *
   * Read off the season's engine rather than the league's name. Asking
   * `theme === 'vitalli'` left Premier Pearls without the note, and Pearls
   * needs it most: their breaker IS the deciding set, so a player's set count
   * looks wrong to anyone who has not been told why.
   */
  const system = league?.seasons.find((s) => s.id === meta?.seasonId)?.scoringSystem;
  const playoffNeeded = rows?.some((r) => r.needsPlayoff) ?? false;

  return (
    <>
    {/*
      * The intake, on the page a visitor actually lands on.
      *
      * /join is the link the league shares, but somebody who finds the league
      * through a player's story and wants in has no reason to guess a URL.
      * Top of the table rather than the foot: a newcomer does not scroll past
      * eleven squads to find out whether they can join.
      */}
    {/* The season's winners, once its Final is saved as finished. First,
        because after a Final it is the one thing every visitor came to see. */}
    <ChampionsCard seasonId={seasonId} seasonName={meta?.season} />
    {/* Team owners entering a squad for the next season — the Society's
        Season 3. Its own card, because it asks for a side, not a player. */}
    {/* Gone once entries close — an invitation to a closed list is a
        dead end. */}
    {league?.squadIntake && !(league.squadDeadline && Date.parse(league.squadDeadline) < Date.now()) && (
      <section className="card join-call">
        {league.squadLogoUrl && <img className="season3-logo" src={league.squadLogoUrl} alt={`${league.squadIntake} badge`} />}
        <div className="card-head"><h2>Interested in joining {league.squadIntake}?</h2></div>
        {league.squadSeasonRuns && <p className="season3-runs">Played <b>{league.squadSeasonRuns}</b>.</p>}
        <p className="muted">
          New teams: enter your team and its 9-player squad, with each player&rsquo;s Playtomic
          and actual rating. Ratings count as stars, and the squad limit is 23 stars.
          {league.squadDeadline && <> Entries close on <b>{closesOn(league.squadDeadline)}</b>.</>}
        </p>
        {/* Gone once current sides have closed; the card stays for new teams. */}
        {!(league.returnDeadline && Date.parse(league.returnDeadline) < Date.now()) && (
          <p className="muted small">
            Current teams confirm through their captain&rsquo;s link instead
            {league.returnDeadline ? <>, by <b>{closesOn(league.returnDeadline)}</b></> : null}.
          </p>
        )}
        <div className="join-actions">
          <a className="btn primary" href={`${leagueBase()}/squad`}>Enter a squad</a>
        </div>
      </section>
    )}
    {league?.interestIntake && (
      <section className="card join-call">
        <div className="card-head"><h2>Join {league.name}</h2></div>
        <p className="muted">
          {league.interestIntake}. Places are being allocated now &mdash; leave your details
          and the league will be in touch.
        </p>
        <div className="join-actions">
          <a className="btn primary" href={`${leagueBase()}/join`}>Register your interest</a>
          {ownerTermsFor(league.slug) && (
            <a className="btn" href={`${leagueBase()}/owners`}>Buy a team</a>
          )}
        </div>
      </section>
    )}
    <section className="card">
      <div className="card-head">
        <h2>{meta?.season ?? 'Standings'}</h2>
        {meta?.archived && <span className="badge">Archived</span>}
      </div>

      {error && <p className="muted">Could not load standings: {error}</p>}
      {!rows && !error && <p className="muted">Loading…</p>}

      {rows && rows.length === 0 && (
        <p className="muted">No matches played yet — the table appears once results are in.</p>
      )}

      {rows && rows.length > 0 && (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Team</th>
                  <th className="num">P</th>
                  <th className="num">Pts</th>
                  {/*
                   * SD and GD, not "Set ±" and "Game ±".
                   *
                   * A column is only ever as narrow as its widest cell, and
                   * these two headers were wider than any number beneath them —
                   * together they held about 55px hostage in a table that has
                   * roughly 310px to work with. The name column paid for it:
                   * four of the six Premier Pearls sides wrapped onto two lines,
                   * and the crest had been shrunk to 1.35rem to make room.
                   *
                   * The abbreviations are what league tables have always used
                   * for a difference, and the note below the table names them in
                   * full, which is where a reader who does not know them looks.
                   */}
                  <th className="num">SD</th>
                  <th className="num">GD</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.team_id}>
                    {/* Sides the rules cannot separate share a position. */}
                    <td className="rank">{row.position ?? i + 1}</td>
                    <td>
                      <span className="team-cell">
                        <Crest url={row.logo_url} colour={row.colour} name={row.team_name} />
                        {/*
                          Name and badge stack, so the badge never competes with
                          the name for width. Inline, it pushed longer names onto
                          a second line while short ones stayed on one, and the
                          rows stopped lining up with each other.
                        */}
                        <span className="team-label">
                          <span>{shortName(row.team_name)}</span>
                          {row.needsPlayoff && <span className="badge-warn playoff">playoff</span>}
                          {/*
                            The minimum-games rule, under the name where the
                            playoff badge goes — a deduction is a fact about the
                            side, and putting it beside the points would leave a
                            reader working out which number already includes it.

                            Two voices on purpose. Once it has landed it is a
                            number: −10, already in the points. While it can
                            still be fixed it is a job: three more games. The
                            league asked for this to be visible so there is no
                            confusion, and the version that prevents confusion
                            is the one a captain reads in round eight.
                          */}
                          {/*
                            ONLY WHEN A PLAYER CAN NO LONGER GET THERE.

                            This read the pro-rata shortfall, which asks a
                            player for as many games as their side has played.
                            A night seats six of nine, so three are always
                            behind and the gap grows by three a round before
                            rotation pulls it back — every team in the league
                            wore "3 games short" after one match day, and every
                            captain asked what they had done wrong. Nothing, and
                            there was nothing any of them could do about it.

                            `atRisk` is the number that can be acted on: the
                            minimum, less games played, less rounds left. Silent
                            while a squad can still get there, and once the
                            league stage is over it is the deduction itself.
                          */}
                          {row.minGames &&
                            (row.minGames.applied
                              ? row.minGames.deduction > 0
                              : row.minGames.atRisk.length > 0) && (
                              <span
                                className={row.minGames.applied ? 'badge-warn mg' : 'badge-soft mg'}
                                title={(row.minGames.applied
                                  ? row.minGames.short
                                  : row.minGames.atRisk
                                )
                                  .map((p) =>
                                    row.minGames!.applied
                                      ? `${p.name}: played ${p.played}, ${p.short} short`
                                      : `${p.name}: played ${p.played}, cannot now reach the minimum (${p.short} short at best)`,
                                  )
                                  .join('\n')}
                              >
                                {row.minGames.applied
                                  ? `−${row.minGames.deduction} min. games`
                                  : `−${row.minGames.unavoidable} min. games locked in`}
                              </span>
                            )}
                        </span>
                      </span>
                    </td>
                    <td className="num">{row.played}</td>
                    <td className="num strong">{row.points}</td>
                    {/*
                      Running differentials, as the organiser asked for. Both
                      stay visible at every width — hiding one behind a
                      breakpoint was what left an unexplained nought on a phone.
                    */}
                    <td className="num muted-cell">{signed(row.set_difference)}</td>
                    <td className="num muted-cell">{signed(row.game_difference)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            The rule itself, in words, once per table. A badge saying "−10 min.
            games" only means something to somebody who already knows the rule,
            and the point of showing any of this is that nobody has to.
          */}
          {meta?.minGames && (
            <p className="muted foot-note">
              Every player must play at least <strong>{meta.minGames.minGames}</strong> league
              games. Each game short costs the side{' '}
              <strong>{meta.minGames.penaltyPerGame}</strong>{' '}
              {meta.minGames.penaltyPerGame === 1 ? 'point' : 'points'}
              {meta.seasonComplete
                ? ', and those deductions are in the points above.'
                : '. Nothing is deducted until the last league round has been played, so a side marked short can still fix it.'}
            </p>
          )}

          {(meta?.tiebreakers.length ?? 0) > 0 && (
            <p className="muted foot-note">
              <strong>SD</strong> and <strong>GD</strong> are set and game difference — sets and
              games won minus lost across the season, where 0 means level. Sides equal on points
              are separated by{' '}
              {meta!.tiebreakers.map((t) => TIEBREAKER_LABEL[t] ?? readable(t)).join(', then ')}.
              {system === 'vitalli' && (
                <>
                  {' '}
                  A Super Tie-Break counts as a set only when it decides a game — that is, when the
                  two sets are split. After a 2–0 it decides the bonus point, not a set, and it
                  never counts toward games.
                </>
              )}
              {system === 'rooftop' && (
                <>
                  {' '}
                  The 10-point tie-break is not a third set. It decides a match that finished one
                  set each, so that match stands at 1–1 on sets, and it never counts toward games.
                </>
              )}
              {system === 'interclub' && (
                <>
                  {' '}
                  Points are added up across the nights: 3 for a match won 2–0, 2–1 when a
                  10-point tie-break decides it, 18 a night. The tie-break is not a third set, and
                  it never counts toward games.
                </>
              )}
              {system === 'pearls' && (
                <>
                  {' '}
                  A Super Tie-Break is the deciding set, so it counts as one. It is only played
                  when the two sets are split, and it never counts toward games.
                </>
              )}
            </p>
          )}

          {playoffNeeded && (
            /*
             * A league that has written down how it plays off says so here in
             * its own words. The generic sentence is what we say when it has
             * not — it names the remedy without describing it, which is enough
             * to explain a shared position but not enough to settle one.
             */
            <p className="notice warn">
              Sides sharing a position are level on every criterion the rules can compute.{' '}
              {league?.playoffFormat ?? 'A sudden-death playoff separates them.'}
            </p>
          )}

          {/*
            The table as a post.
            
            Offered to everyone, as the fixture list's poster is: every figure
            on it is already on this screen, so it is a nicer rendering of what
            anyone could screenshot. Hidden until two sides have played, since
            a table of noughts ranks nobody.
          */}
          {league && rows && hasPostableTable(rows) && (
            /* No fallback: the button simply appears when its code lands.
               A spinner where a share button is about to be would draw the eye
               to something nobody was waiting for. */
            <Suspense fallback={null}>
              <TableShare
                league={league}
                rows={rows}
                heading={meta?.season ?? 'The table'}
                seasonLogoUrl={seasonLogoUrl}
                sponsorLogoUrls={sponsorLogoUrls}
              />
            </Suspense>
          )}
        </>
      )}
    </section>

      {/*
        Below the table, and below the share button, because this is the part a
        player scrolls TO. The table answers how a side is doing; this answers
        where a person is.
      */}
      <SeasonStats seasonId={seasonId} league={league} />

      {/*
        Last, because it is the thing a player comes looking for rather than
        the thing they arrived at. The rules and the seed list are read once a
        season and then referred back to; the table and the stats are read every
        week. Above them, this card would be in the way of both.
      */}
      <LeagueDocs
        seasonId={seasonId}
        league={league}
        scoringSystem={system}
        seasonName={meta?.season}
      />
    </>
  );
}
